/**
 * DPI Calculator Tool
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

function num(v: unknown, fallback: number): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export const DpiCalculatorTool: ITransformerTool = {
  id: 'dpi-calculator',
  name: 'DPI Calculator',
  description: 'Calculate DPI or pixel dimensions from physical size',
  version: '1.0.0',
  type: 'transformer',
  category: 'Calculators',
  keywords: ['dpi', 'ppi', 'pixels', 'inches', 'print', 'calculator'],

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'mode',
      type: 'select',
      label: 'Mode',
      defaultValue: 'dpi-from-size',
      options: ['dpi-from-size', 'pixels-from-dpi'],
      description: 'Compute DPI from pixels+inches, or compute pixels from inches+DPI',
    },
    { name: 'widthPx', type: 'number', label: 'Width (px)', defaultValue: 1920, validation: { min: 1, max: 100000 } },
    { name: 'heightPx', type: 'number', label: 'Height (px)', defaultValue: 1080, validation: { min: 1, max: 100000 } },
    { name: 'widthIn', type: 'number', label: 'Width (in)', defaultValue: 13.3, validation: { min: 0.1, max: 1000, step: 0.1 } },
    { name: 'heightIn', type: 'number', label: 'Height (in)', defaultValue: 7.5, validation: { min: 0.1, max: 1000, step: 0.1 } },
    { name: 'dpi', type: 'number', label: 'DPI', defaultValue: 300, validation: { min: 1, max: 2400 } },
  ],

  async transform(_input: string, config: Record<string, unknown>): Promise<string> {
    const mode = String(config.mode ?? 'dpi-from-size');
    const widthPx = num(config.widthPx, 0);
    const heightPx = num(config.heightPx, 0);
    const widthIn = num(config.widthIn, 0);
    const heightIn = num(config.heightIn, 0);
    const dpi = num(config.dpi, 0);

    if (mode === 'pixels-from-dpi') {
      if (dpi <= 0 || widthIn <= 0 || heightIn <= 0) throw ToolError.validation('Provide DPI and physical size in inches');
      const outW = Math.round(widthIn * dpi);
      const outH = Math.round(heightIn * dpi);
      return `Pixels: ${outW} x ${outH}\nDPI: ${dpi}\nSize: ${widthIn}in x ${heightIn}in`;
    }

    if (widthPx <= 0 || heightPx <= 0 || widthIn <= 0 || heightIn <= 0) {
      throw ToolError.validation('Provide pixel dimensions and physical size in inches');
    }

    const dpiX = widthPx / widthIn;
    const dpiY = heightPx / heightIn;
    const dpiAvg = (dpiX + dpiY) / 2;
    return `DPI (X): ${dpiX.toFixed(2)}\nDPI (Y): ${dpiY.toFixed(2)}\nDPI (avg): ${dpiAvg.toFixed(2)}\nPixels: ${widthPx} x ${heightPx}\nSize: ${widthIn}in x ${heightIn}in`;
  },
};
